import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import api, { formatApiError, NEXUS_LOGO } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Sparkles, Users, ArrowRight, ShieldCheck, Wallet, Star, Ticket } from "lucide-react";
import SEO from "../components/SEO";
import { safeGet, safeSet } from "../lib/safeStorage";
import { SignupProof } from "../components/ResultsWall";

const COUNTRIES = [
  ["GB", "United Kingdom", "GBP"], ["US", "United States", "USD"], ["IE", "Ireland", "EUR"], ["CA", "Canada", "CAD"], ["AU", "Australia", "AUD"],
  ["NZ", "New Zealand", "NZD"], ["FR", "France", "EUR"], ["DE", "Germany", "EUR"], ["ES", "Spain", "EUR"], ["IT", "Italy", "EUR"], ["NL", "Netherlands", "EUR"],
  ["BE", "Belgium", "EUR"], ["PT", "Portugal", "EUR"], ["AT", "Austria", "EUR"], ["CH", "Switzerland", "CHF"], ["SE", "Sweden", "SEK"], ["NO", "Norway", "NOK"],
  ["DK", "Denmark", "DKK"], ["PL", "Poland", "PLN"], ["AE", "United Arab Emirates", "USD"], ["IN", "India", "INR"], ["SG", "Singapore", "SGD"], ["HK", "Hong Kong", "HKD"],
  ["JP", "Japan", "JPY"], ["ZA", "South Africa", "ZAR"], ["BR", "Brazil", "BRL"], ["MX", "Mexico", "MXN"],
];

const GOOGLE_LOGO = "https://www.gstatic.com/images/branding/product/1x/gsa_512dp.png";

function googleLogin(role) {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirect = `${window.location.origin}/auth/callback${role ? `?role=${role}` : ""}`;
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
}

export default function Auth() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { login, signup, user, loading } = useAuth();

  const mode = params.get("mode") === "login" ? "login" : "signup";
  const initialRole = params.get("role") === "brand" ? "brand" : params.get("role") === "creator" ? "creator" : "";
  const [role, setRole] = useState(initialRole);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [country, setCountry] = useState("GB");
  const [busy, setBusy] = useState(false);
  const partnerFromUrl = (params.get("partner") || "").toLowerCase().replace(/[^a-z0-9_-]/g, "");
  const [iiaOptIn, setIiaOptIn] = useState(false);
  const [talentOptIn, setTalentOptIn] = useState(false);
  const [talentCats, setTalentCats] = useState([]);
  const TALENT = [["actor", "Actor"], ["voice", "Voice-over"], ["singer", "Singer"], ["dancer", "Dancer"], ["model", "Model"], ["athlete", "Athlete"], ["presenter", "Presenter / host"], ["performer", "Live performer"]];
  const refCode = params.get("ref") || "";
  const srcFromUrl = (params.get("src") || "").toLowerCase().replace(/[^a-z0-9_-]/g, "").slice(0, 60);
  if (srcFromUrl) safeSet("nexus_signup_source", srcFromUrl);
  const signupSource = srcFromUrl || safeGet("nexus_signup_source") || "";
  const promoFromUrl = (params.get("promo") || "").toUpperCase().replace(/[^A-Z0-9_-]/g, "").slice(0, 32);
  const [promoCode, setPromoCode] = useState(promoFromUrl);

  useEffect(() => {
    // Handle OAuth callback return — provider redirects here with ?oauth_token=<jwt>
    const oauthToken = params.get("oauth_token");
    const oauthError = params.get("oauth_error");
    const oauthProvider = params.get("oauth_provider");
    const oauthNew = params.get("oauth_new");
    if (oauthError) {
      const msg = {
        suspended: "This account is suspended. Contact support@collabnexus.co.uk",
        not_configured: `${oauthProvider || "That provider"} sign-in isn't switched on yet.`,
        token_exchange_failed: "Sign-in failed at the provider. Try again.",
        invalid_state: "Session expired — please try signing in again.",
        business_account_required: "Instagram sign-in needs a Business or Creator account. Switch your account type in the Instagram app (Settings → Account type) and try again.",
        profile_fetch_failed: "We couldn't read your profile from the provider. Please try again.",
        access_denied: "You cancelled the sign-in.",
      }[oauthError] || `Sign-in failed (${oauthError})`;
      toast.error(msg);
      // Clear the query params to prevent re-toast on refresh
      setParams({}, { replace: true });
      return;
    }
    if (oauthToken) {
      try {
        localStorage.setItem("nexus_token", oauthToken);
        toast.success(oauthNew ? "Welcome to Nexus!" : "Welcome back");
        // Trigger auth context refresh by full navigation
        window.location.href = "/creator";
      } catch {
        toast.error("Could not complete sign-in — please try again");
      }
      return;
    }
    if (loading) return;
    if (user) navigate(user.role === "brand" ? "/brand" : "/creator");
  }, [user, navigate, loading, params, setParams]);

  const canSubmit = useMemo(() => {
    if (mode === "login") return email && password.length >= 6;
    return email && password.length >= 6 && name && role;
  }, [mode, email, password, name, role]);

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit || busy) return;
    setBusy(true);
    try {
      if (mode === "login") {
        const u = await login(email, password);
        toast.success(`Welcome back, ${u.name}`);
        navigate(u.role === "brand" ? "/brand" : "/creator");
      } else {
        const u = await signup({ email, password, name, role, company: role === "brand" ? company : null, referral_code: refCode || null, signup_source: signupSource || null, country, partner_opt_in: role === "creator" && (iiaOptIn || talentOptIn) ? ["iia"] : [], talent_interest: role === "creator" && talentOptIn ? { representation: true, categories: talentCats } : null });
        toast.success(`Welcome to Nexus, ${u.name}`);
        // Auto-redeem a promo code if the brand entered one at signup
        const code = (promoCode || "").trim();
        if (code && u.role === "brand") {
          try {
            const r = await api.post("/promo/redeem", { code });
            toast.success(`🏆 Unlocked ${r.data?.months || ""} months of Brand Pro`, {
              description: "Every Pro feature is on us until it expires.",
              duration: 6000,
            });
          } catch (pe) {
            // Non-fatal — user is still signed up, just show the reason
            toast.error(formatApiError(pe.response?.data?.detail) || "Promo code couldn't be applied", { duration: 6000 });
          }
        }
        navigate(u.role === "brand" ? "/brand" : "/creator");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grain grid lg:grid-cols-2">
      <SEO
        title={mode === "login" ? "Log in" : "Sign up"}
        description="Sign in to Nexus or create a Creator or Brand account."
        path="/auth"
        noIndex
      />
      {/* Left: Brand panel */}
      <aside className="hidden lg:flex flex-col justify-between p-12 border-r border-white/5 relative overflow-hidden">
        <div className="absolute -top-32 -left-24 h-96 w-96 rounded-full bg-nexus-pink/15 blur-3xl" />
        <div className="absolute -bottom-32 -right-24 h-96 w-96 rounded-full bg-nexus-blue/15 blur-3xl" />

        <Link to="/" className="relative flex items-center gap-3 z-10" data-testid="auth-logo-link">
          <img src={NEXUS_LOGO} alt="Nexus" className="h-10 w-10 object-contain" />
          <span className="font-display font-bold text-xl tracking-tight">NEXUS</span>
        </Link>

        <div className="relative z-10 space-y-8 max-w-md">
          <h2 className="font-display font-bold text-4xl tracking-tighter leading-tight">
            Join the marketplace where{" "}
            <span className="nexus-gradient-text">every deal is protected</span>.
          </h2>
          <div className="space-y-4">
            {[
              { i: ShieldCheck, t: "Escrow on every hire", d: "Funds are held until content is approved." },
              { i: Star, t: "5-category reviews", d: "Reputation grows from real work, not vanity metrics." },
              { i: Wallet, t: "Direct payouts", d: "Auto-release the moment brands approve." },
            ].map((f) => {
              const Icon = f.i;
              return (
                <div key={f.t} className="flex gap-4">
                  <div className="h-10 w-10 rounded-lg glass flex items-center justify-center shrink-0">
                    <Icon className="h-5 w-5 text-nexus-blue" />
                  </div>
                  <div>
                    <div className="font-semibold">{f.t}</div>
                    <div className="text-sm text-zinc-400">{f.d}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="text-xs uppercase tracking-[0.3em] text-zinc-600 relative z-10">
          Connect · Collab · Create
        </div>
      </aside>

      {/* Right: Form */}
      <main className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img src={NEXUS_LOGO} alt="Nexus" className="h-8 w-8" />
            <span className="font-display font-bold text-lg">NEXUS</span>
          </div>

          <div className="mb-8">
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">
              {mode === "login" ? "Welcome back" : "Get started"}
            </div>
            <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tight">
              {mode === "login" ? "Log in to Nexus" : "Create your account"}
            </h1>
          </div>

          {mode === "signup" && signupSource.startsWith("event") && (
            <div className="mb-4 p-3 rounded-xl bg-nexus-pink/10 border border-nexus-pink/30 text-sm text-zinc-200" data-testid="event-entry-banner">
              🎟️ You're entering the <span className="font-bold text-nexus-pink">London weekend draw</span> — create your account and you're in to win a free website.
            </div>
          )}
          {mode === "signup" && refCode && (
            <div className="mb-4 p-3 rounded-xl border border-nexus-purple/40 bg-nexus-purple/10 text-xs text-zinc-200 flex items-center gap-2" data-testid="auth-ref-banner">
              🎁 Referral <span className="font-mono font-bold text-nexus-pink">{refCode}</span> applied — {role === "creator" ? "your friend earns a 2.5% bonus from Nexus on your deals; you still keep 100%." : "your friend earns a free month when you upgrade."}
            </div>
          )}

          {mode === "signup" && (
            <div className="grid grid-cols-2 gap-3 mb-6">
              <button
                type="button"
                onClick={() => setRole("creator")}
                data-testid="role-creator-btn"
                className={`p-4 rounded-xl border text-left transition-colors ${
                  role === "creator"
                    ? "border-nexus-purple bg-nexus-purple/10"
                    : "border-white/10 hover:border-white/25 bg-white/[0.02]"
                }`}
              >
                <Sparkles className="h-5 w-5 text-nexus-pink mb-3" />
                <div className="font-semibold text-sm">I&apos;m a Creator</div>
                <div className="text-xs text-zinc-400 mt-1">Get hired for paid collabs</div>
              </button>
              <button
                type="button"
                onClick={() => setRole("brand")}
                data-testid="role-brand-btn"
                className={`p-4 rounded-xl border text-left transition-colors ${
                  role === "brand"
                    ? "border-nexus-purple bg-nexus-purple/10"
                    : "border-white/10 hover:border-white/25 bg-white/[0.02]"
                }`}
              >
                <Users className="h-5 w-5 text-nexus-blue mb-3" />
                <div className="font-semibold text-sm">I&apos;m a Brand</div>
                <div className="text-xs text-zinc-400 mt-1">Discover & hire creators</div>
              </button>
            </div>
          )}

          {mode === "signup" && <SignupProof role={role} />}
          <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
            {(mode === "signup" ? role === "brand" : true) && (
              <button
                type="button"
                onClick={() => googleLogin(mode === "signup" ? role || "creator" : null)}
                data-testid="google-login-btn"
                className="w-full h-12 rounded-full bg-white text-black font-semibold flex items-center justify-center gap-3 hover:bg-white/90 transition-colors"
              >
                <img src={GOOGLE_LOGO} alt="" className="h-5 w-5" />
                {mode === "signup" ? "Continue with Google (brands)" : "Continue with Google"}
              </button>
            )}
            {(mode === "signup" ? role !== "brand" : true) && (
              <div className="grid grid-cols-3 gap-2" data-testid="social-signup-row">
                {[
                  { key: "instagram", label: "Instagram", bg: "bg-gradient-to-br from-[#f09433] via-[#e6683c] to-[#bc1888]" },
                  { key: "tiktok", label: "TikTok", bg: "bg-black border border-white/20" },
                  { key: "youtube", label: "YouTube", bg: "bg-[#FF0000]" },
                ].map((s) => (
                  <button
                    key={s.key}
                    type="button"
                    onClick={async () => {
                      try {
                        const r = await api.get(`/oauth/${s.key}/start`, { params: { role: role || "creator" } });
                        if (r.data?.authorize_url) window.location.href = r.data.authorize_url;
                      } catch (err) {
                        const detail = err.response?.data?.detail || "";
                        if (err.response?.status === 501) {
                          toast.info(`${s.label} sign-in is being switched on — use email for now.`);
                        } else {
                          toast.error(detail || `Could not start ${s.label} sign-in`);
                        }
                      }
                    }}
                    data-testid={`social-signup-${s.key}`}
                    className={`h-11 rounded-full text-white text-xs font-semibold flex items-center justify-center gap-1.5 opacity-90 hover:opacity-100 transition-opacity ${s.bg}`}
                    title={`Continue with ${s.label}`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            )}
            <div className="flex items-center gap-3 text-xs uppercase tracking-widest text-zinc-500">
              <div className="h-px flex-1 bg-white/10" />
              or with email
              <div className="h-px flex-1 bg-white/10" />
            </div>
            {mode === "signup" && (
              <div>
                <Label htmlFor="name" className="text-xs uppercase tracking-widest text-zinc-400">
                  {role === "brand" ? "Your name" : "Display name"}
                </Label>
                <Input
                  id="name"
                  data-testid="auth-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={role === "brand" ? "Alex Morgan" : "Jamie Rivers"}
                  className="mt-1.5 bg-white/[0.02] border-white/10 text-white placeholder:text-zinc-600 h-11"
                />
              </div>
            )}
            {mode === "signup" && role === "brand" && (
              <div>
                <Label htmlFor="company" className="text-xs uppercase tracking-widest text-zinc-400">
                  Company
                </Label>
                <Input
                  id="company"
                  data-testid="auth-company-input"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  placeholder="Acme Studios"
                  className="mt-1.5 bg-white/[0.02] border-white/10 text-white placeholder:text-zinc-600 h-11"
                />
              </div>
            )}
            {mode === "signup" && role === "brand" && (
              <div>
                <Label htmlFor="promo" className="text-xs uppercase tracking-widest text-zinc-400 flex items-center gap-1.5">
                  <Ticket className="h-3 w-3 text-nexus-pink" /> Promo code
                  <span className="text-zinc-600 normal-case tracking-normal ml-1">(optional)</span>
                </Label>
                <div className="relative mt-1.5">
                  <Input
                    id="promo"
                    data-testid="auth-promo-input"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, "").slice(0, 32))}
                    placeholder="e.g. WELCOMEPRO3M"
                    className="bg-white/[0.02] border-white/10 text-white placeholder:text-zinc-600 h-11 uppercase font-mono tracking-wider pl-9"
                  />
                  <Ticket className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-nexus-pink pointer-events-none" />
                </div>
                {promoCode && (
                  <p className="text-[10px] uppercase tracking-widest text-emerald-300 mt-1.5 flex items-center gap-1">
                    <Sparkles className="h-2.5 w-2.5" /> Applied on sign-up — Pro unlocks instantly
                  </p>
                )}
              </div>
            )}
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-widest text-zinc-400">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="auth-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@nexus.com"
                className="mt-1.5 bg-white/[0.02] border-white/10 text-white placeholder:text-zinc-600 h-11"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-widest text-zinc-400">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="auth-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 6 characters"
                className="mt-1.5 bg-white/[0.02] border-white/10 text-white placeholder:text-zinc-600 h-11"
              />
            </div>
            {mode === "signup" && (
              <div>
                <Label htmlFor="country" className="text-xs uppercase tracking-widest text-zinc-400">Country</Label>
                <select
                  id="country"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  data-testid="auth-country-select"
                  className="mt-1.5 w-full h-11 rounded-md bg-[#0C0C10] border border-white/10 text-white text-sm px-3"
                >
                  {COUNTRIES.map(([code, label, cur]) => <option key={code} value={code}>{label} · {cur}</option>)}
                </select>
                <p className="text-[11px] text-zinc-500 mt-1">Sets the currency prices are shown in. Payments are processed in GBP.</p>
              </div>
            )}

            {mode === "signup" && role === "creator" && (
              <label className={`flex items-start gap-3 rounded-2xl border p-3 cursor-pointer transition-colors ${iiaOptIn ? "border-nexus-pink/40 bg-nexus-pink/5" : "border-white/10 bg-white/[0.02] hover:bg-white/[0.04]"}`} data-testid="iia-opt-in-label">
                <input type="checkbox" checked={iiaOptIn} onChange={(e) => setIiaOptIn(e.target.checked)} className="mt-1 h-4 w-4 accent-[#ff2f6e]" data-testid="iia-opt-in-checkbox" />
                <span className="text-xs text-zinc-300 leading-relaxed">
                  <span className="text-white font-semibold">Also join the International Idols Agency Creator Network</span>{partnerFromUrl === "iia" ? " (you came from IIA — tick to link your accounts)" : ""}. I agree to Nexus sharing my name, email, country, niches and platforms with International Idols Agency (USA) so they can create my Creator Network profile.{" "}
                  <a href="https://internationalidolsagency.com" target="_blank" rel="noreferrer" className="underline text-zinc-400 hover:text-white">About IIA</a>
                </span>
              </label>
            )}
            {mode === "signup" && role === "creator" && (
              <div className={`rounded-2xl border p-3 transition-colors ${talentOptIn ? "border-sky-400/40 bg-sky-500/5" : "border-white/10 bg-white/[0.02]"}`} data-testid="talent-opt-in-box">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" checked={talentOptIn} onChange={(e) => setTalentOptIn(e.target.checked)} className="mt-1 h-4 w-4 accent-[#38bdf8]" data-testid="talent-opt-in-checkbox" />
                  <span className="text-xs text-zinc-300 leading-relaxed"><span className="text-white font-semibold">I'm also open to acting, voice, modelling or performance work</span> — put me forward to International Idols Agency for representation and casting opportunities (same data-sharing consent as above).</span>
                </label>
                {talentOptIn && (
                  <div className="flex flex-wrap gap-1.5 mt-3 pl-7" data-testid="talent-categories">
                    {TALENT.map(([k, label]) => (
                      <button type="button" key={k} onClick={() => setTalentCats((c) => c.includes(k) ? c.filter((x) => x !== k) : [...c, k])}
                        className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${talentCats.includes(k) ? "bg-sky-500/20 border-sky-400/50 text-sky-100" : "border-white/10 text-zinc-400 hover:text-white"}`} data-testid={`talent-cat-${k}`}>{label}</button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <Button
              type="submit"
              disabled={!canSubmit || busy}
              data-testid="auth-submit-btn"
              className="nexus-gradient text-white font-semibold rounded-full h-12 w-full hover:opacity-90 border-0"
            >
              {busy ? "Please wait…" : mode === "login" ? "Log in" : "Create account"}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </form>

          <div className="mt-6 text-sm text-zinc-400 text-center">
            {mode === "login" ? (
              <>
                New to Nexus?{" "}
                <button
                  className="text-white underline underline-offset-4 hover:text-nexus-pink"
                  onClick={() => setParams({ mode: "signup" })}
                  data-testid="switch-to-signup"
                >
                  Create an account
                </button>
              </>
            ) : (
              <>
                Already have an account?{" "}
                <button
                  className="text-white underline underline-offset-4 hover:text-nexus-blue"
                  onClick={() => setParams({ mode: "login" })}
                  data-testid="switch-to-login"
                >
                  Log in
                </button>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
